import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, FlaskConical, Loader2, Search, SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import PaperCard, { PaperCardSkeleton } from "@/components/PaperCard";
import EmptyState from "@/components/EmptyState";
import { AppliedFilters, FilterGroups } from "@/components/filters";
import { useMeta } from "@/lib/useMeta";
import { fetchPaperPage } from "@/lib/papers";
import { useTaxonomies } from "@/lib/taxonomies";
import { activeTaxonomies, tagOf } from "@/lib/taxonomy";
import { toBn } from "@/lib/format";
import { APP_URL } from "@/lib/config";

const PAGE_SIZE = 24;
const SORTS = [
  { value: "newest", label: "নতুন আগে" },
  { value: "most-copied", label: "সবচেয়ে বেশি কপি হওয়া" },
  { value: "most-viewed", label: "সবচেয়ে বেশি দেখা" },
];
const RESERVED = new Set(["q", "sort"]); // URL-এর এই দুই নাম ক্যাটাগরির key হিসেবে নেওয়া হয় না

const gridCls = "grid gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-3";

export default function Papers() {
  useMeta({
    title: "প্রশ্নপত্র খুঁজুন — মুমতাহিন গ্যালারি",
    description: "শিক্ষকদের শেয়ার করা প্রশ্নপত্র — শ্রেণি, বিষয় আর পরীক্ষার ধরন দিয়ে খুঁজুন, দেখে নিন, নিজের একাউন্টে কপি করুন।",
  });

  // অবস্থা URL-এ: ফিল্টার করা তালিকার লিংক WhatsApp/Facebook গ্রুপে শেয়ার করা যায়, back দিলে একই তালিকায় ফেরা যায়
  const [params, setParams] = useSearchParams();
  const taxonomies = useTaxonomies();
  const filterable = useMemo(() => activeTaxonomies(taxonomies).filter((x) => !RESERVED.has(x.key)), [taxonomies]);

  const urlQ = params.get("q") ?? "";
  const sort = SORTS.some((s) => s.value === params.get("sort")) ? params.get("sort") : "newest";
  const filters = useMemo(() => {
    const f = {};
    for (const x of filterable) {
      const v = params.get(x.key);
      if (v && x.terms.some((m) => m.value === v)) f[x.key] = v;
    }
    return f;
  }, [params, filterable]);

  // বাছা ফিল্টারগুলো "key:value" ট্যাগ — মান/key-তে কমা থাকতে পারে না, তাই join/split নিরাপদ
  const tagsKey = Object.entries(filters)
    .map(([key, value]) => tagOf(key, value))
    .join(",");

  const patch = (changes) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const toggleFilter = (key, value) => patch({ [key]: filters[key] === value ? "" : value });
  const clearAll = () => {
    pushedQ.current = "";
    setQInput("");
    const next = new URLSearchParams();
    if (sort !== "newest") next.set("sort", sort);
    setParams(next, { replace: true });
  };

  // সার্চ বক্স: টাইপ করা মান আলাদা state-এ, একটু থেমে URL-এ — প্রতি অক্ষরে কোয়েরি/URL ঝাঁকুনি এড়াতে
  const [qInput, setQInput] = useState(urlQ);
  const pushedQ = useRef(urlQ);
  useEffect(() => {
    if (urlQ !== pushedQ.current) {
      pushedQ.current = urlQ;
      setQInput(urlQ);
    }
  }, [urlQ]);
  useEffect(() => {
    const trimmed = qInput.trim();
    if (trimmed === pushedQ.current.trim()) return;
    const h = setTimeout(() => {
      pushedQ.current = trimmed;
      const next = new URLSearchParams(window.location.search);
      if (trimmed) next.set("q", trimmed);
      else next.delete("q");
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(h);
  }, [qInput, setParams]);

  // তালিকা আনা। "লোড হচ্ছে" = শেষ ফলাফল এই কোয়েরির না। পুরনো কোয়েরির দেরিতে আসা উত্তর ফেলে দেওয়া হয়।
  const queryKey = `${urlQ}|${tagsKey}|${sort}`;
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState(null); // { key, rows, total } | { key, error }
  const [more, setMore] = useState({ key: "", rows: [], busy: false, failed: false });

  useEffect(() => {
    let cancelled = false;
    fetchPaperPage({ search: urlQ, tags: tagsKey ? tagsKey.split(",") : [], sort, limit: PAGE_SIZE })
      .then(({ rows, total }) => !cancelled && setResult({ key: `${queryKey}|${attempt}`, rows, total }))
      .catch(() => !cancelled && setResult({ key: `${queryKey}|${attempt}`, error: true }));
    return () => {
      cancelled = true;
    };
  }, [queryKey, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const fullKey = `${queryKey}|${attempt}`;
  const fresh = result?.key === fullKey;
  const failed = fresh && result.error;
  const loading = !fresh;
  const base = result && !result.error ? result.rows : null;
  const extra = more.key === fullKey ? more.rows : [];
  const rows = base ? [...base, ...extra.filter((p) => !base.some((b) => b.id === p.id))] : null;
  const total = result && !result.error ? result.total : 0;

  const loadMore = async () => {
    setMore((m) => ({ ...(m.key === fullKey ? m : { key: fullKey, rows: [] }), busy: true, failed: false }));
    try {
      const page = await fetchPaperPage({
        search: urlQ,
        tags: tagsKey ? tagsKey.split(",") : [],
        sort,
        limit: PAGE_SIZE,
        offset: rows.length,
      });
      setMore((m) => ({ key: fullKey, rows: [...(m.key === fullKey ? m.rows : []), ...page.rows], busy: false, failed: false }));
    } catch {
      setMore((m) => ({ ...m, key: fullKey, busy: false, failed: true }));
    }
  };

  // চালু ফিল্টারের চিপ
  const chips = [
    urlQ && { id: "q", label: `“${urlQ}”`, onRemove: () => { pushedQ.current = ""; setQInput(""); patch({ q: "" }); } },
    ...filterable
      .filter((x) => filters[x.key])
      .map((x) => ({
        id: x.key,
        label: x.terms.find((m) => m.value === filters[x.key])?.label_bn ?? filters[x.key],
        onRemove: () => patch({ [x.key]: "" }),
      })),
  ].filter(Boolean);
  const filterCount = Object.keys(filters).length;

  const [sheetOpen, setSheetOpen] = useState(false);

  const sortSelect = (
    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <span className="hidden sm:inline">সাজান:</span>
      <select
        value={sort}
        onChange={(e) => patch({ sort: e.target.value === "newest" ? "" : e.target.value })}
        aria-label="সাজানোর ধরন"
        className="h-9 rounded-lg border border-input bg-card px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {SORTS.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="container pb-6 pt-6 sm:pt-10">
      <header className="mb-5 sm:mb-7">
        <h1 className="font-display text-2xl font-bold text-foreground sm:text-3xl">প্রশ্নপত্র খুঁজুন</h1>
        <p className="mt-1.5 max-w-2xl text-muted-foreground">
          শিক্ষকদের শেয়ার করা প্রশ্নপত্র। শ্রেণি, বিষয় বা পরীক্ষার ধরন দিয়ে ছেঁকে নিন, দেখে পছন্দ হলে নিজের একাউন্টে কপি করুন।
        </p>
      </header>

      <div className="lg:grid lg:grid-cols-[17.5rem_minmax(0,1fr)] lg:items-start lg:gap-8">
        {/* ডেস্কটপ ফিল্টার সাইডবার */}
        <aside className="no-print sticky top-24 hidden max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain rounded-2xl border border-border bg-card p-5 lg:block" aria-label="ফিল্টার">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-display text-base font-semibold">ফিল্টার</h2>
            {chips.length > 0 && (
              <button type="button" onClick={clearAll} className="rounded-md text-sm text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                সব মুছুন
              </button>
            )}
          </div>
          <FilterGroups taxonomies={filterable} filters={filters} onToggle={toggleFilter} />
        </aside>

        <section aria-label="ফলাফল" className="min-w-0">
          {/* সার্চ + (মোবাইলে) ফিল্টার বাটন — ছোট স্ক্রিনে স্ক্রল করলেও উপরে আটকে থাকে */}
          <div className="no-print sticky top-16 z-20 -mx-4 border-b border-border bg-background/90 px-4 py-3 backdrop-blur-md lg:static lg:mx-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none">
            <div className="flex gap-2">
              <div className="relative flex-1" role="search">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <input
                  type="search"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder="নাম, প্রতিষ্ঠান বা শিক্ষক"
                  aria-label="প্রশ্নপত্র খুঁজুন"
                  className="h-11 w-full rounded-xl border border-input bg-card pl-10 pr-10 text-[15px] text-ellipsis shadow-card placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
                />
                {qInput && (
                  <button
                    type="button"
                    aria-label="লেখা মুছুন"
                    onClick={() => setQInput("")}
                    className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>
              <Button variant="outline" className="h-11 shrink-0 gap-2 lg:hidden" onClick={() => setSheetOpen(true)}>
                <SlidersHorizontal aria-hidden="true" />
                ফিল্টার
                {filterCount > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs text-primary-foreground">
                    {toBn(filterCount)}
                  </span>
                )}
              </Button>
            </div>
          </div>

          <AppliedFilters chips={chips} onClear={clearAll} className="no-print mt-4" />

          <div className="no-print mb-4 mt-4 flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" aria-live="polite">
              {loading ? "খোঁজা হচ্ছে…" : failed ? "" : `${toBn(total)}টি প্রশ্নপত্র`}
            </p>
            {sortSelect}
          </div>

          {failed ? (
            <EmptyState
              icon={AlertTriangle}
              title="প্রশ্নপত্র লোড করা যাচ্ছে না"
              actions={<Button onClick={() => setAttempt((n) => n + 1)}>আবার চেষ্টা করুন</Button>}
            >
              ইন্টারনেট সংযোগ দেখে নিয়ে আবার চেষ্টা করুন।
            </EmptyState>
          ) : !rows ? (
            <div className={gridCls} aria-busy="true">
              {Array.from({ length: 6 }, (_, i) => (
                <PaperCardSkeleton key={i} />
              ))}
            </div>
          ) : rows.length === 0 ? (
            chips.length > 0 ? (
              <EmptyState
                title="এই খোঁজে কোনো প্রশ্নপত্র মেলেনি"
                actions={
                  <>
                    <Button onClick={clearAll}>সব ফিল্টার মুছুন</Button>
                    <Button asChild variant="outline">
                      <Link to="/try">
                        <FlaskConical aria-hidden="true" />
                        নিজে বানিয়ে দেখুন
                      </Link>
                    </Button>
                  </>
                }
              >
                অন্য শব্দে খুঁজুন, বা ফিল্টার কমিয়ে দেখুন — ওপরের চিপে ট্যাপ করলে একটা একটা করে সরবে।
              </EmptyState>
            ) : (
              <EmptyState
                title="এখনো কোনো প্রশ্নপত্র শেয়ার হয়নি"
                actions={
                  <Button asChild>
                    <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                      মূল অ্যাপে যান
                    </a>
                  </Button>
                }
              >
                প্রথম প্রশ্নপত্রটা আপনার হাত থেকেই আসতে পারে — মূল অ্যাপে প্রশ্নপত্রের মেনু থেকে "গ্যালারিতে শেয়ার করুন" চাপুন।
              </EmptyState>
            )
          ) : (
            <>
              <div className={`${gridCls} transition-opacity ${loading ? "opacity-50" : ""}`} aria-busy={loading}>
                {rows.map((p) => (
                  <PaperCard key={p.id} paper={p} />
                ))}
              </div>

              {rows.length < total && (
                <div className="no-print mt-8 flex flex-col items-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    {toBn(total)}টির মধ্যে {toBn(rows.length)}টি দেখানো হচ্ছে
                  </p>
                  <Button variant="outline" size="lg" onClick={loadMore} disabled={more.busy || loading}>
                    {more.busy && <Loader2 className="animate-spin" aria-hidden="true" />}
                    আরও দেখান
                  </Button>
                  {more.failed && <p className="text-sm text-destructive">আরও আনা যায়নি — আবার চেষ্টা করুন।</p>}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* মোবাইল ফিল্টার শিট */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="flex max-h-[88vh] flex-col gap-0 rounded-t-3xl p-0">
          <div className="flex items-center justify-between border-b border-border px-5 py-4 pr-14">
            <SheetTitle className="font-display text-lg">ফিল্টার</SheetTitle>
            <SheetDescription className="sr-only">শ্রেণি, বিষয়, পরীক্ষার ধরন ইত্যাদি দিয়ে প্রশ্নপত্র ছেঁকে নিন</SheetDescription>
            {chips.length > 0 && (
              <button type="button" onClick={clearAll} className="text-sm text-primary underline-offset-4 hover:underline">
                সব মুছুন
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <FilterGroups taxonomies={filterable} filters={filters} onToggle={toggleFilter} />
          </div>
          <div className="border-t border-border bg-card px-5 pb-safe pt-3">
            <Button size="lg" className="w-full" onClick={() => setSheetOpen(false)}>
              {loading ? "খোঁজা হচ্ছে…" : `${toBn(total)}টি প্রশ্নপত্র দেখুন`}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
